(() => {
  const COLOUR_SWATCHES_NAME = "product-colour-swatches";
  const SECTION_CACHE_LIMIT = 24;
  const GALLERY_PRELOAD_COUNT = 2;

  /** @type {Map<string, Promise<Document>>} section_id URL -> in-flight or settled document */
  const sectionCache = new Map();
  const readySections = new Set();

  const prefersSaveData = () =>
    Boolean(navigator.connection && navigator.connection.saveData);

  class ProductColourSwatches extends HTMLElement {
    #boundClick = null;
    #boundPrefetch = null;
    #boundPopState = null;
    #abortController = null;
    #loading = false;
    #requestId = 0;
    #optimisticToken = 0;

    connectedCallback() {
      this.#boundClick = (event) => this.#onClick(event);
      this.#boundPrefetch = (event) => this.#onPrefetch(event);
      this.#boundPopState = () => this.#onPopState();

      this.#syncColourwayHrefs();
      this.addEventListener("click", this.#boundClick);
      this.addEventListener("pointerover", this.#boundPrefetch);
      this.addEventListener("focusin", this.#boundPrefetch);
      this.addEventListener("pointerdown", this.#boundPrefetch);
      window.addEventListener("popstate", this.#boundPopState);
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.#boundClick);
      this.removeEventListener("pointerover", this.#boundPrefetch);
      this.removeEventListener("focusin", this.#boundPrefetch);
      this.removeEventListener("pointerdown", this.#boundPrefetch);
      window.removeEventListener("popstate", this.#boundPopState);

      // Do not abort while a colourway load is in flight — replacing
      // MainContent disconnects this element mid-request.
      if (!this.#loading) {
        this.#abortController?.abort();
      }
    }

    #syncColourwayHrefs() {
      for (const swatch of this.querySelectorAll("[data-colour-swatch]")) {
        const url = colourwayUrl(swatch);

        if (url) swatch.setAttribute("href", url);
      }
    }

    prefetchColourway(productUrl, sourceEl) {
      if (!productUrl || prefersSaveData()) return;

      if (sourceEl instanceof Element) {
        preloadSpecs(specsFromSource(sourceEl));
      }

      const sectionIds = this.#sectionIds();

      if (sectionIds.length === 0) return;

      Promise.all(
        sectionIds.map((sectionId) =>
          this.#requestSection(productUrl, sectionId),
        ),
      ).catch(() => {
        // Prefetch failures are silent — the click path reports them.
      });
    }

    #onClick(event) {
      const swatch = event.target.closest("[data-colour-swatch]");

      if (!swatch || !this.contains(swatch)) return;

      if (swatch.getAttribute("aria-current") === "true") {
        event.preventDefault();
        return;
      }

      const productUrl = colourwayUrl(swatch);

      if (!productUrl) return;

      event.preventDefault();
      this.loadColourway(productUrl, { pushState: true, source: swatch });
    }

    #onPrefetch(event) {
      const swatch = event.target.closest("[data-colour-swatch]");

      if (!swatch || !this.contains(swatch)) return;
      if (swatch.getAttribute("aria-current") === "true") return;

      if (event.type === "pointerover") {
        const from = event.relatedTarget;

        if (from instanceof Node && swatch.contains(from)) return;
      }

      const productUrl = colourwayUrl(swatch);

      if (!productUrl) return;

      this.prefetchColourway(productUrl, swatch);
    }

    #onPopState() {
      const href = window.location.href;

      // Soft-swap only works for product documents. Collection/home/etc. need a
      // full load so section scripts run (importNode does not execute scripts).
      if (!this.#isProductPath(href)) {
        window.location.assign(href);
        return;
      }

      this.loadColourway(href, { pushState: false });
    }

    #isProductPath(url) {
      try {
        return new URL(url, window.location.origin).pathname.includes(
          "/products/",
        );
      } catch {
        return false;
      }
    }

    async loadColourway(productUrl, { pushState = true, source = null } = {}) {
      if (!document.getElementById("MainContent")) {
        window.location.href = productUrl;
        return;
      }

      this.#abortController?.abort();

      const controller = new AbortController();
      const requestId = this.#requestId + 1;

      this.#abortController = controller;
      this.#requestId = requestId;
      this.#loading = true;
      this.#optimisticToken += 1;

      const optimisticToken = this.#optimisticToken;

      if (
        source instanceof Element &&
        !this.#isSectionReady(productUrl) &&
        !prefersSaveData()
      ) {
        this.#startOptimisticGallery(source, optimisticToken);
      }

      this.setAttribute("aria-busy", "true");

      const mainContent = document.getElementById("MainContent");

      mainContent?.setAttribute("aria-busy", "true");
      // aria-busy is for assistive tech; the data attribute is what the
      // progress bar hangs off, so the styling never leaks to other features
      // that mark MainContent busy.
      mainContent?.setAttribute("data-colourway-loading", "");

      const scrollY = window.scrollY;

      // Mobile only (PO, 23 Sep): below 1024 the gallery stacks above the form
      // and scrolls away, so a colour chosen from the swatches would otherwise
      // change an image the shopper cannot see. From 1024 up the gallery is
      // sticky beside the form (main-product, same breakpoint) and stays in
      // view, so the page holds its position there, as it always did.
      const returnToTop =
        pushState && !window.matchMedia("(min-width: 1024px)").matches;

      // Scroll anchoring is switched off for the whole swap when returning to
      // the top, not just the glide: the swapped-in sections settle while the
      // page is moving, and Chromium's anchoring pulled the position back up
      // by as much as 200px to hold them still — a blip before the glide.
      // WebKit does not anchor, so this is a no-op there. Restored a second
      // after the glide starts.
      if (returnToTop) {
        document.documentElement.style.overflowAnchor = "none";
      }

      const isCurrentRequest = () =>
        this.#requestId === requestId && this.#abortController === controller;

      try {
        // Render only the product-dependent sections rather than the whole
        // document. This includes the main product section and PDP doorways,
        // whose metaobject cards can differ between colourway products.
        // A full PDP render (recommendations, reviews, UGC, footer) is the bulk
        // of the wait on a colourway swap. Falls back to the document swap if
        // any required section id is unknown or a section request fails.
        const swapped = await this.#swapProductSections(
          productUrl,
          controller.signal,
        );

        if (!isCurrentRequest() || controller.signal.aborted) return;

        if (!swapped) {
          // Soft-swap MainContent via HTML fetch. Avoid ?sections= with
          // Accept: application/json — that returns product JSON, not section
          // HTML, and large multi-section requests 400/502 on theme dev.
          const doc = await this.#fetchDocument(productUrl, controller.signal);

          if (!isCurrentRequest() || controller.signal.aborted) return;

          const main = document.getElementById("MainContent");

          if (!main || !main.isConnected) {
            window.location.href = productUrl;
            return;
          }

          this.#syncDocumentHead(doc);
          this.#replaceMainContent(main, doc);
        }

        if (!isCurrentRequest()) return;

        const cleanUrl = new URL(productUrl, window.location.origin);

        cleanUrl.searchParams.delete("sections");
        cleanUrl.searchParams.delete("section_id");

        if (pushState) {
          window.history.pushState({}, "", cleanUrl.toString());
        }

        const nextMain = document.getElementById("MainContent");
        const titleNode = nextMain?.querySelector("[data-product-title]");
        const nextTitle = titleNode?.getAttribute("data-product-title");

        if (nextTitle) {
          document.title = nextTitle;
        }

        // On the stacked layout a deliberate colour change returns to the top
        // of the product, where the new colour's gallery is. Everywhere else —
        // the sticky desktop layout, and history navigation, where Back is
        // expected to restore the position the shopper left — the page holds.
        if (returnToTop) {
          // Smooth, at the PO's request (21 Sep): the ticket is about how the
          // switch feels, and a snap to the top read as abrupt. Reduce Motion
          // keeps it instant.
          const reduceMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)",
          ).matches;

          window.setTimeout(() => {
            document.documentElement.style.overflowAnchor = "";
          }, 1000);

          window.scrollTo({
            top: 0,
            left: 0,
            behavior: reduceMotion ? "auto" : "smooth",
          });
        } else {
          window.scrollTo(0, scrollY);
        }

        document.dispatchEvent(
          new CustomEvent("colourway:change", {
            detail: { url: cleanUrl.toString() },
          }),
        );
      } catch (error) {
        if (error?.name === "AbortError") return;

        if (isCurrentRequest()) {
          window.location.href = productUrl;
        }
      } finally {
        // Aborted/superseded requests must not clear busy state for the
        // in-flight successor, or rapid clicks can race URL vs content.
        if (isCurrentRequest()) {
          this.#loading = false;
          this.removeAttribute("aria-busy");

          const main = document.getElementById("MainContent");

          main?.removeAttribute("aria-busy");
          main?.removeAttribute("data-colourway-loading");
        }
      }
    }

    #isSectionReady(productUrl) {
      const sectionIds = this.#sectionIds();

      if (sectionIds.length === 0) return false;

      return sectionIds.every((sectionId) =>
        readySections.has(this.#sectionRequestUrl(productUrl, sectionId)),
      );
    }

    #startOptimisticGallery(sourceEl, token) {
      const specs = specsFromSource(sourceEl);

      if (specs.length === 0) return;

      const gallery = document.querySelector("product-gallery");

      if (!gallery) return;

      const targets = galleryImageTargets(gallery);

      specs.forEach((spec, index) => {
        const target = targets[index];

        if (!target) return;

        const probe = new Image();

        if (spec.srcset) probe.srcset = spec.srcset;
        if (spec.sizes) probe.sizes = spec.sizes;
        probe.src = spec.src;

        const apply = () => {
          if (this.#optimisticToken !== token) return;
          if (!target.isConnected) return;

          if (spec.srcset) {
            target.srcset = spec.srcset;
          } else {
            target.removeAttribute("srcset");
          }

          if (spec.sizes) target.sizes = spec.sizes;
          target.src = spec.src;
          if (spec.alt) target.alt = spec.alt;
        };

        if (probe.complete && probe.naturalWidth > 0) {
          apply();
          return;
        }

        probe
          .decode()
          .then(apply)
          .catch(() => {
            probe.addEventListener("load", apply, { once: true });
          });
      });
    }

    #sectionIds() {
      const sectionIds = new Set();
      const section = this.closest(".shopify-section");
      const id = section?.id || "";

      if (id.startsWith("shopify-section-")) {
        sectionIds.add(id.slice("shopify-section-".length));
      }

      for (const doorway of document.querySelectorAll(
        "[data-product-doorways]",
      )) {
        const doorwaySection = doorway.closest(".shopify-section");
        const doorwayId = doorwaySection?.id || "";

        if (!doorwayId.startsWith("shopify-section-")) continue;

        sectionIds.add(doorwayId.slice("shopify-section-".length));
      }

      return [...sectionIds];
    }

    #sectionRequestUrl(productUrl, sectionId) {
      const requestUrl = new URL(productUrl, window.location.origin);

      requestUrl.searchParams.set("section_id", sectionId);

      return requestUrl.toString();
    }

    #requestSection(productUrl, sectionId) {
      const key = this.#sectionRequestUrl(productUrl, sectionId);
      const cached = sectionCache.get(key);

      if (cached) return cached;

      const request = fetch(key, {
        headers: { Accept: "text/html" },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              `Colourway section fetch failed: ${response.status}`,
            );
          }

          return response.text();
        })
        .then((markup) => {
          const doc = new DOMParser().parseFromString(markup, "text/html");

          readySections.add(key);
          preloadGalleryImages(doc);
          return doc;
        });

      request.catch(() => {
        if (sectionCache.get(key) === request) {
          sectionCache.delete(key);
          readySections.delete(key);
        }
      });

      rememberSection(key, request);
      return request;
    }

    async #swapProductSections(productUrl, signal) {
      const sectionIds = this.#sectionIds();

      if (sectionIds.length === 0) return false;

      const sections = sectionIds.map((sectionId) =>
        document.getElementById(`shopify-section-${sectionId}`),
      );

      if (sections.some((section) => !section)) return false;

      let docs;

      try {
        docs = await Promise.all(
          sectionIds.map((sectionId) =>
            this.#requestSection(productUrl, sectionId),
          ),
        );
      } catch (error) {
        if (error?.name === "AbortError") throw error;
        return false;
      }

      if (signal.aborted) return false;

      const replacements = docs.map((doc, index) => {
        const section = sections[index];
        const sectionId = sectionIds[index];
        const next =
          doc.getElementById(`shopify-section-${sectionId}`) ??
          doc.querySelector(".shopify-section");

        return { section, next };
      });

      if (
        replacements.some(({ section, next }) => !next || !section.isConnected)
      ) {
        return false;
      }

      // Replace the section containing this custom element last so its
      // disconnect does not interrupt the remaining swaps.
      for (const { section, next } of replacements.reverse()) {
        section.replaceWith(document.importNode(next, true));
      }

      return true;
    }

    async #fetchDocument(productUrl, signal) {
      const requestUrl = new URL(productUrl, window.location.origin);

      const response = await fetch(requestUrl.toString(), {
        signal,
        headers: {
          Accept: "text/html",
        },
      });

      if (!response.ok) {
        throw new Error(`Colourway document fetch failed: ${response.status}`);
      }

      const html = await response.text();
      return new DOMParser().parseFromString(html, "text/html");
    }

    #replaceMainContent(main, doc) {
      const nextMain = doc.getElementById("MainContent");

      if (!nextMain) {
        throw new Error("Colourway document missing MainContent");
      }

      main.replaceWith(document.importNode(nextMain, true));
    }

    #syncDocumentHead(doc) {
      if (doc.title) {
        document.title = doc.title;
      }

      const metaSelectors = [
        'meta[property="og:title"]',
        'meta[property="og:url"]',
        'meta[property="og:description"]',
        'meta[property="og:image"]',
        'meta[name="description"]',
        'meta[name="twitter:title"]',
        'meta[name="twitter:description"]',
        'link[rel="canonical"]',
      ];

      for (const selector of metaSelectors) {
        const next = doc.querySelector(selector);
        const current = document.querySelector(selector);

        if (!next || !current) continue;

        if (selector.startsWith("link")) {
          const href = next.getAttribute("href");

          if (href) current.setAttribute("href", href);
        } else {
          const content = next.getAttribute("content");

          if (content != null) current.setAttribute("content", content);
        }
      }

      this.#syncShopifyAnalyticsMeta(doc);
    }

    #syncShopifyAnalyticsMeta(doc) {
      // Klarna OSM (and other app blocks) read purchase amount from
      // ShopifyAnalytics.meta — sync it from the fetched colourway document.
      if (!window.ShopifyAnalytics) return;

      window.ShopifyAnalytics.meta = window.ShopifyAnalytics.meta || {};

      for (const script of doc.querySelectorAll("script")) {
        const text = script.textContent || "";

        if (
          !text.includes("ShopifyAnalytics.meta") ||
          !text.includes("var meta =")
        ) {
          continue;
        }

        const start = text.indexOf("var meta =");

        if (start === -1) continue;

        const jsonStart = text.indexOf("{", start);

        if (jsonStart === -1) continue;

        let depth = 0;
        let end = -1;

        for (let i = jsonStart; i < text.length; i += 1) {
          const char = text[i];

          if (char === "{") depth += 1;
          if (char === "}") depth -= 1;

          if (depth === 0) {
            end = i + 1;
            break;
          }
        }

        if (end === -1) continue;

        try {
          const meta = JSON.parse(text.slice(jsonStart, end));

          for (const key of Object.keys(meta)) {
            window.ShopifyAnalytics.meta[key] = meta[key];
          }
        } catch {
          // Ignore malformed analytics payloads and keep going.
        }

        break;
      }
    }
  }

  function colourwayUrl(sourceEl) {
    if (!(sourceEl instanceof Element)) return "";

    const template = sourceEl.querySelector("[data-gallery-preload]");
    const variantUrl = template?.getAttribute("data-colour-variant-url");

    return variantUrl || sourceEl.getAttribute("href") || "";
  }

  function rememberSection(key, request) {
    if (sectionCache.has(key)) {
      sectionCache.delete(key);
    }

    sectionCache.set(key, request);

    while (sectionCache.size > SECTION_CACHE_LIMIT) {
      const oldest = sectionCache.keys().next().value;

      sectionCache.delete(oldest);
      readySections.delete(oldest);
    }
  }

  function specsFromSource(sourceEl) {
    if (!(sourceEl instanceof Element)) return [];

    const template = sourceEl.matches("[data-gallery-preload]")
      ? sourceEl
      : sourceEl.querySelector("[data-gallery-preload]");
    const raw = template?.getAttribute("data-gallery-images");

    if (!raw) return [];

    try {
      const specs = JSON.parse(raw);

      if (!Array.isArray(specs)) return [];

      return specs
        .filter((spec) => spec && spec.src)
        .slice(0, GALLERY_PRELOAD_COUNT)
        .map((spec) => ({
          src: spec.src,
          srcset: spec.srcset || "",
          sizes: spec.sizes || "",
          alt: spec.alt || "",
        }));
    } catch {
      return [];
    }
  }

  function imageSpecsFromList(images) {
    const specs = [];

    for (const img of images) {
      if (specs.length >= GALLERY_PRELOAD_COUNT) break;

      const src = img.getAttribute("src");

      if (!src) continue;

      specs.push({
        src,
        srcset: img.getAttribute("srcset") || "",
        sizes: img.getAttribute("sizes") || "",
        alt: img.getAttribute("alt") || "",
      });
    }

    return specs;
  }

  function preloadSpecs(specs) {
    if (prefersSaveData()) return;

    for (const spec of specs) {
      const preloader = new Image();

      if (spec.srcset) preloader.srcset = spec.srcset;
      if (spec.sizes) preloader.sizes = spec.sizes;
      preloader.src = spec.src;
    }
  }

  function galleryImageTargets(gallery) {
    const wrapper = gallery.querySelector(".swiper-wrapper");

    if (!wrapper) return [];

    const swiperImages = [
      ...wrapper.querySelectorAll(
        ".swiper-slide-active .product-gallery__image, .swiper-slide-next .product-gallery__image",
      ),
    ];

    if (swiperImages.length > 0) return swiperImages;

    const images = [];

    for (const slide of wrapper.querySelectorAll(".product-gallery__slide")) {
      const img = slide.querySelector(".product-gallery__image");

      if (!img) continue;

      images.push(img);
      if (images.length >= GALLERY_PRELOAD_COUNT) break;
    }

    return images;
  }

  function preloadGalleryImages(doc) {
    const gallery = doc.querySelector("product-gallery");

    if (!gallery) {
      preloadSpecs(
        imageSpecsFromList(doc.querySelectorAll(".product-gallery__image")),
      );
      return;
    }

    const slides = [...gallery.querySelectorAll(".product-gallery__slide")];
    const featuredMediaId = featuredMediaIdFromGallery(gallery);
    let start = 0;

    if (featuredMediaId) {
      const index = slides.findIndex(
        (slide) => Number(slide.dataset.mediaId) === featuredMediaId,
      );

      if (index >= 0) start = index;
    }

    const images = [];

    for (let i = start; i < slides.length; i += 1) {
      const img = slides[i].querySelector(".product-gallery__image");

      if (!img) continue;

      images.push(img);
      if (images.length >= GALLERY_PRELOAD_COUNT) break;
    }

    preloadSpecs(imageSpecsFromList(images));
  }

  function featuredMediaIdFromGallery(gallery) {
    const variantId = Number(gallery.dataset.variantId);
    const page = gallery.closest("product-page");
    const jsonNode = page?.querySelector("[data-product-json]");

    if (!variantId || !jsonNode) return null;

    try {
      const product = JSON.parse(jsonNode.textContent);
      const variant = product.variants?.find(
        (item) => Number(item.id) === variantId,
      );

      return Number(variant?.featured_media?.id) || null;
    } catch {
      return null;
    }
  }

  if (!customElements.get(COLOUR_SWATCHES_NAME)) {
    customElements.define(COLOUR_SWATCHES_NAME, ProductColourSwatches);
  }
})();
